import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { ApiConnectionString } from "../constants/ApiConntection";
import LoadingSpinner from "../components/LoadingSpinner";

export default function ExtraSectionsSection({ user }) {
  const lastSavedRef = useRef({});
  const abortRef = useRef({});
  const [extraSections, setExtraSections] = useState([]);

  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [addingSection, setAddingSection] = useState(false);
  const [savingId, setSavingId] = useState(null);

  const fetchData = async () => {
    if (!user) return;

    setIsLoading(true);
    setSubmitError("");
    setSubmitSuccess(false);

    try {
      const response = await axios.get(`${ApiConnectionString}/api/PractitionerExtraSection`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });

      const list = Array.isArray(response.data) ? response.data : [];
      setExtraSections(list);

      lastSavedRef.current = list.reduce((acc, s) => {
        if (s.id) {
          acc[s.id] = {
            title: s.title ?? "",
            content: s.content ?? "",
          };
        }
        return acc;
      }, {});
    } catch (err) {
      setSubmitSuccess(false);
      setSubmitError(err?.response?.data || err?.message || "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user) fetchData();
  }, [user]);

  const updateExtraSection = (index, key, value) => {
    setExtraSections((prev) => prev.map((s, i) => (i === index ? { ...s, [key]: value } : s)));
  };

  const addExtraSection = async () => {
    if (!user) return;

    setAddingSection(true);
    setSubmitError("");
    setSubmitSuccess(false);

    try {
      const response = await axios.post(`${ApiConnectionString}/api/PractitionerExtraSection`, null, {
        headers: { Authorization: `Bearer ${user.token}` },
      });

      if (response.status === 200) {
        const newSection = {
          id: response.data.id,
          sortOrder: response.data.sortOrder,
          title: "",
          content: "",
        };

        setExtraSections((prev) => [...prev, newSection]);

        if (newSection.id) {
          lastSavedRef.current = {
            ...lastSavedRef.current,
            [newSection.id]: { title: "", content: "" },
          };
        }

        setSubmitSuccess(true);
      }
    } catch (err) {
      setSubmitSuccess(false);
      setSubmitError(err?.response?.data || err?.message || "Something went wrong");
    } finally {
      setAddingSection(false);
    }
  };

  const removeExtraSection = async (index) => {
    if (!user) return;

    const target = extraSections[index];
    if (!target) return;

    setSubmitError("");
    setSubmitSuccess(false);

    if (!target.id) {
      setExtraSections((prev) => prev.filter((_, i) => i !== index));
      return;
    }

    setRemovingId(target.id);
    try {
      await axios.delete(`${ApiConnectionString}/api/PractitionerExtraSection/${target.id}`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });

      setExtraSections((prev) => prev.filter((_, i) => i !== index));

      const copy = { ...lastSavedRef.current };
      delete copy[target.id];
      lastSavedRef.current = copy;

      setSubmitSuccess(true);
      setSubmitError("");
    } catch (err) {
      setSubmitSuccess(false);
      setSubmitError(err?.response?.data || err?.message || "Something went wrong");
    } finally {
      setRemovingId(null);
    }
  };

  const saveExtraSectionOnBlur = async (sectionId) => {
    if (!user) return;
    if (!sectionId) return;

    const current = extraSections.find((x) => x.id === sectionId);
    if (!current) return;

    const prev = lastSavedRef.current[sectionId] || {};
    const next = {
      title: current.title ?? "",
      content: current.content ?? "",
    };

    const changed = next.title !== (prev.title ?? "") || next.content !== (prev.content ?? "");
    if (!changed) return;

    if (abortRef.current[sectionId]) {
      abortRef.current[sectionId].abort();
    }
    abortRef.current[sectionId] = new AbortController();

    setSavingId(sectionId);
    setSubmitError("");
    setSubmitSuccess(false);

    try {
      await axios.put(
        `${ApiConnectionString}/api/PractitionerExtraSection/${sectionId}`,
        { Title: next.title, Content: next.content },
        {
          headers: { Authorization: `Bearer ${user.token}` },
          signal: abortRef.current[sectionId].signal,
        }
      );

      lastSavedRef.current = {
        ...lastSavedRef.current,
        [sectionId]: next,
      };

      setSubmitSuccess(true);
      setSubmitError("");
    } catch (err) {
      const aborted =
        err?.name === "CanceledError" || err?.name === "AbortError" || err?.code === "ERR_CANCELED";
      if (aborted) return;

      setSubmitSuccess(false);
      setSubmitError(err?.response?.data || err?.message || "Something went wrong");
    } finally {
      setSavingId(null);
    }
  };

  if (isLoading) {
    return (
      <div>
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="row g-3">
      <div className="col-12">
        <div className="mb-3">
          <div className="text-muted">Optional extra sections you would like on the site.</div>
          <div className="text-muted small">We save each section when you leave a field.</div>
        </div>

        {submitError ? <div className="alert alert-danger">{submitError}</div> : null}
        {submitSuccess ? <div className="alert alert-success">Saved</div> : null}

        {extraSections.length === 0 ? (
          <div className="border rounded p-3 text-muted">
            No extra sections added yet. Press Add section to create one.
          </div>
        ) : null}
      </div>

      <div className="col-12 d-grid gap-3">
        {extraSections.map((s, i) => (
          <div key={s.id || i} className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="row g-3">
                <div className="col-12 col-md-4">
                  <label className="form-label">Section title</label>
                  <div className="form-text">For example Fees and policies, FAQs, Location, How I work.</div>
                  <input
                    className="form-control"
                    required
                    value={s.title ?? ""}
                    onChange={(e) => updateExtraSection(i, "title", e.target.value)}
                    onBlur={() => saveExtraSectionOnBlur(s.id)}
                    placeholder="For example FAQs"
                    disabled={removingId === s.id}
                  />
                  <div className="invalid-feedback">Please fill this in or type NA</div>
                </div>

                <div className="col-12 col-md-8">
                  <label className="form-label">Content</label>
                  <div className="form-text">This can be a short paragraph or a few lines.</div>
                  <textarea
                    className="form-control"
                    rows="4"
                    required
                    value={s.content ?? ""}
                    onChange={(e) => updateExtraSection(i, "content", e.target.value)}
                    onBlur={() => saveExtraSectionOnBlur(s.id)}
                    placeholder="Write what you would like shown in this section"
                    disabled={removingId === s.id}
                  />
                  <div className="invalid-feedback">Please fill this in or type NA</div>
                </div>

                <div className="col-12 d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => removeExtraSection(i)}
                    disabled={removingId === s.id}
                  >
                    {removingId === s.id ? "Removing" : "Remove"}
                  </button>

                  {savingId === s.id ? (
                    <div className="text-muted small align-self-center">Saving</div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="col-12">
        <div className="d-flex justify-content-end">
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={addExtraSection}
            disabled={addingSection}
          >
            {addingSection ? "Adding" : "Add section"}
          </button>
        </div>
      </div>
    </div>
  );
}
